import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/lib/supabase';

interface CompanyComboboxProps {
  value: string;
  onChange: (value: string) => void;
}

export function CompanyCombobox({ value, onChange }: CompanyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the search function
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Debounced search function
  const debouncedSearchCompanies = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setCompanies([]);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name')
          .ilike('name', `%${query}%`)
          .limit(10);

        if (error) throw error;
        setCompanies(data || []);
      } catch (error) {
        console.error('Error searching companies:', error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    }, 300), // 300ms debounce delay
    []
  );

  // Focus the input when the popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Fetch the selected company when the value changes
  useEffect(() => {
    if (value) {
      fetchCompanyById(value);
    }
  }, [value]);

  const fetchCompanyById = async (companyId: string) => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      if (data) {
        setSelectedCompany(data);
        setSearchQuery(data.name);
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  const handleAddCompany = async () => {
    if (!searchQuery.trim()) return;

    try {
      const { data, error } = await supabase
        .from('companies')
        .insert([{ name: searchQuery }])
        .select()
        .single();

      if (error) throw error;
      if (data) {
        onChange(data.id); // Update the form with the new company ID
        setSelectedCompany(data);
        setSearchQuery(data.name);
        setOpen(false);
      }
    } catch (error) {
      console.error('Error adding company:', error);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedCompany?.name || "Select company..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder="Search companies..."
            value={searchQuery}
            onValueChange={(value) => {
              setSearchQuery(value);
              debouncedSearchCompanies(value); // Use debounced search
            }}
            autoFocus
          />
          <CommandList>
            <CommandEmpty>
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                </div>
              ) : searchQuery.length < 2 ? (
                "Type at least 2 characters to search..."
              ) : (
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={handleAddCompany}
                >
                  Add "{searchQuery}" as a new company
                </Button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {companies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={company.id}
                  onSelect={() => {
                    onChange(company.id);
                    setSelectedCompany(company);
                    setSearchQuery(company.name);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === company.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {company.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
